import React from 'react';
import { Link, useLocation } from 'wouter';
import { cn } from '@/lib/utils';
import { Home, FolderKanban, Users, DollarSign, FileText, HelpCircle, Settings, ArrowLeft } from 'lucide-react';

const navItems = [
  { name: "Overview", icon: <Home size={18} />, path: "/admin" },
  { name: "Projects", icon: <FolderKanban size={18} />, path: "/admin/projects" },
  { name: "Users", icon: <Users size={18} />, path: "/admin/users" },
  { name: "Assessments Report", icon: <FileText size={18} />, path: "/admin/assessments-report" },
  { name: "Pricing Matrix", icon: <DollarSign size={18} />, path: "/admin/pricing-matrix" },
  { name: "Pricing Tiers", icon: <Settings size={18} />, path: "/admin/pricing-tiers" },
  { name: "Export History", icon: <FileText size={18} />, path: "/admin/exports" },
  { name: "Help Content", icon: <HelpCircle size={18} />, path: "/admin/help" },
  { name: "Rationale Defaults", icon: <HelpCircle size={18} />, path: "/admin/rationale-defaults" },
  { name: "Access Control", icon: <Users size={18} />, path: "/admin/access" },
  { name: "System Settings", icon: <Settings size={18} />, path: "/admin/system-settings" },
];

interface AdminLayoutProps {
  children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
  const [location] = useLocation();
  const current = navItems.find((n) => location.startsWith(n.path) || (n.path === "/admin" && location === "/admin"));

  return (
    <div className="flex min-h-screen bg-[#0D1224]">
      <aside className="w-64 bg-gray-900 border-r border-gray-700 px-4 py-6 space-y-4">
        <div className="px-2 mb-6">
          <h1 className="text-xl font-bold text-white mb-2">Admin Panel</h1>
          <p className="text-sm text-gray-400">Redwood Value Platform</p>
          
          {/* Back to Home Button */}
          <Link href="/" className="mt-4 block">
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-colors w-full">
              <ArrowLeft size={16} />
              <span>Back to Home</span>
            </button>
          </Link>
        </div>
        
        <nav className="space-y-2">
          {navItems.map((item) => {
            const isActive = location.startsWith(item.path) || (item.path === "/admin" && location === "/admin");
            return (
              <Link
                key={item.name}
                href={item.path}
                data-cy={`admin-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm",
                  isActive 
                    ? "bg-[#4AE2C9]/20 text-[#4AE2C9] font-semibold border border-[#4AE2C9]/30" 
                    : "text-gray-300 hover:bg-gray-800 hover:text-white"
                )}
              >
                {item.icon}
                <span>{item.name}</span>
              </Link>
            );
          })}
        </nav>
      </aside>

      <main className="flex-1 bg-[#0D1224] p-6">
        <div className="text-sm text-gray-400 mb-4 flex items-center gap-2">
          <span>Admin</span>
          <span>/</span>
          <span className="text-[#4AE2C9]">{current?.name || "Section"}</span>
        </div>
        
        <div className="bg-gray-900/50 border border-gray-700 rounded-xl shadow-lg p-6">
          {children}
        </div>
      </main>
    </div>
  );
}
